import type { ReactNode } from 'react';
import { toaster } from '@/components/ui/toaster';
import { isAxiosError } from 'axios';

export const NOTIFICATIONS = {
	HOUSE: {
		CREATE_FAILED: {
			closable: true,
			description: 'Failed to create house. Please try again.',
			type: 'error',
		},
		UPDATE_FAILED: {
			closable: true,
			description: 'Failed to update house. Please try again.',
			type: 'error',
		},
	},
	USER: {
		UPDATED: {
			closable: true,
			description: 'Your profile has been updated successfully.',
			type: 'success',
		},
		UPDATE_FAILED: {
			closable: true,
			description: 'Failed to update profile. Please try again.',
			type: 'error',
		},
	},
	UPLOADS: {
		IN_PROGRESS: {
			title: 'Uploads in progress',
			description: 'Please wait for all uploads to complete or cancel them before proceeding.',
			type: 'warning',
			closable: true,
		},
		CANCELLED: {
			title: 'Upload cancelled',
			type: 'info',
			closable: true,
		},
	},
	SETUP: {
		FINALIZED: {
			type: 'success',
			closable: true,
			description: 'Your house setup has been completed successfully.',
		},
		FINALIZE_FAILED: {
			type: 'error',
			closable: true,
			description: 'Failed to finalize house setup. Please try again.',
		},
	},
	AUTH: {
		INVALID_CREDENTIALS: {
			closable: true,
			description: 'Invalid credentials. Please try again.',
			type: 'error',
		},
		SIGN_IN_FAILED: (error: string) => ({
			closable: true,
			description: `Sign-in failed${error ? ` with : '${error}'` : ''}. Please try again.`,
			type: 'error',
		}),
		GOOGLE_AUTH_FAILED: {
			closable: true,
			description: 'We could not authenticate using Google at the moment. Please try again.',
			type: 'error',
		},
		GOOGLE_SIGN_IN_FAILED: (error: string) => ({
			closable: true,
			description: `Google sign-in failed${error ? ` with : '${error}'` : ''}. Please try again.`,
			type: 'error',
		}),
		MISSING_TOKEN: {
			closable: true,
			description: 'There was an error with the information submitted. Please try again.',
			type: 'error',
		},
		FINALISE_USER_SUCCESS: {
			closable: true,
			description: 'Welcome to bundle!.',
			type: 'success  ',
		},
		FINALISE_USER_FAILED: {
			closable: true,
			description: 'Something went wrong. Please try again or contact support.',
			type: 'error',
		},
	},
	TRANSFER_OWNERSHIP: {
		FAILED: (error?: unknown) => ({
			closable: true,
			description: isAxiosError(error)
				? error.response?.data?.detail
				: 'Failed to send ownership transfer request. Please try again.',
			type: 'error',
		}),
		CANCELLED: {
			closable: true,
			description: 'Ownership transfer has been cancelled successfully.',
			type: 'success',
		},
		CANCEL_FAILED: {
			closable: true,
			description: 'Failed to cancel ownership transfer. Please try again.',
			type: 'error',
		},
	},
	TRANSFER: {
		CLAIM: {
			SUCCESS: {
				closable: true,
				description: 'Property transfer claimed successfully! You are now the owner of this property.',
				type: 'success',
			},
			ERROR: (error?: unknown) => ({
				closable: true,
				description: isAxiosError(error)
					? error.response?.data?.detail
					: 'Failed to claim property transfer. Please try again.',
				type: 'error',
			}),
		},
		RETRY: {
			SUCCESS: {
				closable: true,
				description: 'Transfer finalization has been retriggered. Processing will resume shortly.',
				type: 'success',
			},
			ERROR: (error?: unknown) => ({
				closable: true,
				description: isAxiosError(error)
					? error.response?.data?.detail
					: 'Failed to retry transfer finalization. Please try again.',
				type: 'error',
			}),
		},
	},
};

export const showNotification = (config: Parameters<typeof toaster.create>[0]): string => {
	return toaster.create(config);
};
// ReactNode, not string: toast descriptions may embed a link (see TaskNotification).
export type ShowClosableNotification = (message: ReactNode, type: 'info' | 'success' | 'warning' | 'error') => void;
export const showClosableNotification: ShowClosableNotification = (message, type) => {
	toaster.create({
		description: message,
		type: type,
		closable: true,
	});
};
